import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Loader2, ClipboardList } from "lucide-react";
import { getCoachClientCheckins, retryCoachInsights } from "../lib/api";
import { InsightSections } from "./weekly/WeeklyInsights";
import Dots from "./tracker/Dots";
import CardDetails from "./tracker/CardDetails";
import { sydDateTime } from "../lib/time";

// Coach Weekly Check-In history: every weekly state (Red+Blue/Red/Blue/Green/Orange/Yellow),
// full submission + insights + Stage 1 AI/action data + Coaches Brief / Action Taken / reminder.

const fmt = (iso) => sydDateTime(iso, "None");
const STATUS = { live: "Live", in_process: "In Process", completed: "Completed", closed: "No action required" };

function NeedsAttention({ it, w, clientId }) {
  const [state, setState] = useState("idle");
  const retry = async () => {
    setState("busy");
    try { await retryCoachInsights(clientId, it.submission_id); setState("started"); } catch (_e) { setState("error"); }
  };
  return (
    <div className="space-y-3" data-testid={`coach-insights-needs-attention-${w}`}>
      <p className="font-medium text-brand-ink">Weekly Insights could not be generated automatically</p>
      <p className="text-sm text-brand-ink-muted whitespace-pre-wrap" data-testid={`coach-insights-error-${w}`}>{it.insights?.last_error || "No reason was stored."}</p>
      {state === "started" ? (
        <p className="text-sm text-brand-ink-muted" data-testid={`coach-insights-retry-started-${w}`}>A new attempt has started. Refresh shortly to see the result.</p>
      ) : (
        <button type="button" onClick={retry} disabled={state === "busy"} className="btn-ghost inline-flex items-center gap-2 border border-brand-border rounded-full px-4 py-2 text-sm" data-testid={`coach-insights-retry-btn-${w}`}>
          {state === "busy" ? "Starting..." : "Try again"}
        </button>
      )}
      {state === "error" && <p className="text-sm text-red-700" data-testid={`coach-insights-retry-error-${w}`}>Could not start a new attempt. Please try again.</p>}
    </div>
  );
}

function Submission({ it, w, clientId }) {
  const r = it.satisfaction || {};
  const ins = it.insights || {};
  return (
    <>
      <section>
        <div className="label-eyebrow mb-4">Questions &amp; Answers</div>
        <ol className="space-y-5">
          {it.questions.map((q) => (
            <li key={q.number} data-testid={`coach-checkin-q${q.number}-${w}`}>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-ink-muted">Question {q.number} · {q.category}</p>
              <p className="mt-1 font-medium text-brand-ink">{q.prompt}</p>
              <p className="mt-2 text-brand-ink whitespace-pre-wrap" data-testid={`coach-checkin-a${q.number}-${w}`}>{q.answer}</p>
            </li>
          ))}
          <li data-testid={`coach-checkin-q8-${w}`}>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-ink-muted">Question 8 · Weekly Wins</p>
            <p className="mt-1 font-medium text-brand-ink">{it.question_8?.prompt}</p>
            <dl className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-brand-ink">
              <div><dt className="label-eyebrow">Physical win</dt><dd className="mt-1" data-testid={`coach-checkin-physical-${w}`}>{it.question_8?.physical_win}</dd></div>
              <div><dt className="label-eyebrow">Habit win</dt><dd className="mt-1" data-testid={`coach-checkin-habit-${w}`}>{it.question_8?.habit_win}</dd></div>
              <div><dt className="label-eyebrow">Identity reflection</dt><dd className="mt-1" data-testid={`coach-checkin-identity-${w}`}>{it.question_8?.identity_reflection}</dd></div>
            </dl>
          </li>
        </ol>
      </section>
      <section>
        <div className="label-eyebrow mb-4">Weekly Insights</div>
        {ins.status === "completed" ? (
          <div className="space-y-4"><InsightSections rec={ins} testidPrefix={`coach-insights-${w}`} /></div>
        ) : ins.status === "needs_attention" ? (
          <NeedsAttention it={it} w={w} clientId={clientId} />
        ) : (
          <p className="text-brand-ink-muted" data-testid={`coach-insights-pending-${w}`}>Weekly Insights are still being prepared for this week.</p>
        )}
      </section>
      <section data-testid={`coach-satisfaction-${w}`}>
        <div className="label-eyebrow mb-3">Satisfaction</div>
        <p className="text-brand-ink"><span className="text-brand-ink-muted">Rating:</span> {r.rating ? `${r.rating} / 10` : "not yet rated"}</p>
        {r.feedback && <p className="mt-2 text-brand-ink" data-testid={`coach-feedback-${w}`}><span className="text-brand-ink-muted">Client feedback:</span> {r.feedback}</p>}
        <p className="mt-2 text-brand-ink" data-testid={`coach-support-${w}`}><span className="text-brand-ink-muted">Message to Justine (support box):</span> {it.support_request || "None"}</p>
      </section>
    </>
  );
}

function ActionRecord({ it, w }) {
  const a = it.action;
  const k = it.classification.key;
  const card = { id: `h-${w}`, category: it.classification, missed: it.missed, client_created_at: null, ...(a || {}) };
  card.id = `h-${w}`;
  return (
    <section data-testid={`coach-action-${w}`}>
      <div className="label-eyebrow mb-1">Weekly classification &amp; coaching action</div>
      {k !== "green" && k !== "yellow" && <CardDetails card={card} />}
      {k === "yellow" && (
        <p className="mt-2 text-brand-ink" data-testid={`coach-yellow-setup-${w}`}>
          New Not Set Up: first Weekly Check-In window (grace), no Weekly Check-In submitted.{" "}
          Weekly Check-In Setup: {it.setup?.setup_completed ? `completed ${fmt(it.setup.setup_completed_at)}` : "not completed"}.
        </p>
      )}
      {a ? (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-brand-ink">
          <div><div className="label-eyebrow">Action status</div><p className="mt-1" data-testid={`coach-action-status-${w}`}>{a.status === "ended" ? a.ended_label : STATUS[a.status] || a.status}</p></div>
          <div><div className="label-eyebrow">Completed</div><p className="mt-1" data-testid={`coach-action-completed-${w}`}>{a.completed_at ? fmt(a.completed_at) : "None"}</p></div>
          <div><div className="label-eyebrow">Coaches Brief</div><p className="mt-1 whitespace-pre-wrap" data-testid={`coach-action-brief-${w}`}>{a.coaches_brief || "None"}</p></div>
          <div><div className="label-eyebrow">Action Taken</div><p className="mt-1 whitespace-pre-wrap" data-testid={`coach-action-taken-${w}`}>{a.action_taken || "None"}</p></div>
          {a.reminder && (
            <div className="md:col-span-2" data-testid={`coach-action-reminder-${w}`}>
              <div className="label-eyebrow">Reminder</div>
              <p className="mt-1">“{a.reminder.text}” · {a.reminder.due_date} {a.reminder.due_time}{a.reminder.dismissed_at ? ` · dismissed ${fmt(a.reminder.dismissed_at)}` : ""}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="mt-2 text-sm text-brand-ink-muted">No coaching action record for this week.</p>
      )}
    </section>
  );
}

function Entry({ it, initiallyOpen, clientId }) {
  const [open, setOpen] = useState(initiallyOpen);
  const ref = useRef(null);
  const w = it.weekly_window_identifier;
  const r = it.satisfaction || {};
  useEffect(() => { if (initiallyOpen) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [initiallyOpen]);
  return (
    <div ref={ref} className="surface-card overflow-hidden" data-testid={`coach-checkin-${w}`} data-classification={it.classification.key}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 p-5 text-left" data-testid={`coach-checkin-toggle-${w}`}>
        <div className="min-w-0">
          <div className="font-serif text-2xl text-brand-ink flex items-center gap-3">
            <Dots dots={it.classification.dots} testid={`coach-checkin-dots-${w}`} /> Week {it.week.label}
          </div>
          <div className="text-xs text-brand-ink-muted mt-1 flex flex-wrap gap-3">
            <span data-testid={`coach-checkin-class-${w}`}>{it.classification.label}</span>
            {it.has_submission ? (
              <>
                <span data-testid={`coach-checkin-submitted-${w}`}>Submitted: {fmt(it.submitted_at)}</span>
                <span data-testid={`coach-checkin-rating-${w}`}>Rating: {r.rating ? `${r.rating}/10` : "not yet rated"}</span>
              </>
            ) : <span>No Weekly Check-In submitted</span>}
          </div>
        </div>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-brand-ink-muted shrink-0">
          {open ? "Close" : "View Week"} {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-6 space-y-8" data-testid={`coach-checkin-detail-${w}`}>
          {it.has_submission && <Submission it={it} w={w} clientId={clientId} />}
          <ActionRecord it={it} w={w} />
        </div>
      )}
    </div>
  );
}

function SetupAnswers({ setup }) {
  return (
    <section className="surface-card mt-4 p-6" data-testid="coach-setup-answers">
      <div className="label-eyebrow">Weekly Check-In Setup</div>
      <p className="mt-2 text-brand-ink" data-testid="coach-setup-status">
        {setup?.setup_completed ? `Completed ${fmt(setup.setup_completed_at)}` : "Not completed yet"}
      </p>
      {(setup?.questions || []).map((q) => (
        <div key={q.number} className="mt-5" data-testid={`coach-setup-q${q.number}`}>
          <p className="text-xs uppercase tracking-[0.2em] text-brand-ink-muted">Setup question {q.number}</p>
          <p className="mt-1 font-medium text-brand-ink whitespace-pre-line" data-testid={`coach-setup-q${q.number}-prompt`}>{q.prompt}</p>
          <p className="mt-2 text-brand-ink whitespace-pre-wrap" data-testid={`coach-setup-a${q.number}`}>{q.answer || "None"}</p>
        </div>
      ))}
    </section>
  );
}

export default function CoachCheckinHistory({ clientId }) {
  const [items, setItems] = useState(null);
  const [setup, setSetup] = useState(null);
  const [pending, setPending] = useState(null);
  const [params] = useSearchParams();
  const week = params.get("week");
  useEffect(() => {
    getCoachClientCheckins(clientId, week).then((d) => { setItems(d.items); setSetup(d.setup); setPending(d.pending_week || null); }).catch(() => setItems([]));
  }, [clientId, week]);

  return (
    <section className="mt-8" data-testid="coach-checkin-history">
      <div className="label-eyebrow flex items-center gap-3">
        <span className="inline-block h-px w-10 bg-brand-primary" />
        <ClipboardList size={14} strokeWidth={1.8} /> Weekly Check-In history
      </div>
      {setup && <SetupAnswers setup={setup} />}
      {pending && (
        <div className="mt-4" data-testid="coach-pending-week">
          <p className="text-sm text-brand-ink-muted mb-2" data-testid="coach-pending-week-note">
            This week's Weekly Check-In window hasn't closed yet. Its Weekly History record is created when the window closes (Sunday 11pm Sydney).
          </p>
          <Entry it={pending} initiallyOpen clientId={clientId} />
        </div>
      )}
      {items === null ? (
        <Loader2 className="text-brand-primary animate-spin mt-6" size={24} />
      ) : items.length === 0 ? (
        <p className="surface-card mt-4 p-6 text-brand-ink-muted" data-testid="coach-checkin-empty">No Weekly Check-In history yet.</p>
      ) : (
        <div className="mt-4 space-y-3">{items.map((it) => <Entry key={it.weekly_window_identifier} it={it} initiallyOpen={it.weekly_window_identifier === week} clientId={clientId} />)}</div>
      )}
    </section>
  );
}
